import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from '@Components/Breadcrumbs';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import Heading from '@Components/Heading';
import Loader from '@Components/Loader';
import ShowFor from '@Components/ShowFor';
import {
  CUSTOM_FORMS_QUERY_KEY,
  customFormQueryKey,
  useCustomForms,
} from '@Hooks/useCustomForms';
import type { CustomFormListItem } from '@Types/customForm';
import service from '../../service';

export const CUSTOM_FORMS_PATH = '/custom-form-builder';

const updatedFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export default function CustomForms() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: forms, isLoading, isError } = useCustomForms();
  const [formToDelete, setFormToDelete] = useState<CustomFormListItem | null>(
    null,
  );

  const deleteMutation = useMutation({
    mutationFn: (id: string) => service.deleteCustomForm(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: CUSTOM_FORMS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: customFormQueryKey(id) });
      toastSuccess('Custom form deleted.');
      setFormToDelete(null);
    },
    onError: (e) =>
      toastError(e instanceof Error ? e.message : 'Failed to delete custom form'),
  });

  return (
    <div>
      <Breadcrumbs navList={[{ name: 'Custom Forms', href: CUSTOM_FORMS_PATH }]} />
      <div className="mt-8 mb-4 flex items-center justify-between">
        <Heading>Custom Forms</Heading>
        <ShowFor permissions={['FORM_WRITE']}>
          <Button onClick={() => navigate(`${CUSTOM_FORMS_PATH}/new`)}>
            <Plus className="size-4" />
            New form
          </Button>
        </ShowFor>
      </div>

      {isLoading ? (
        <Loader />
      ) : isError ? (
        <p className="text-sm text-red-600">Custom forms could not be loaded.</p>
      ) : (
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Fields</TableHead>
              <TableHead>Responses</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {forms?.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-zinc-500">
                  No custom forms yet.
                </TableCell>
              </TableRow>
            )}
            {forms?.map((form) => (
              <TableRow key={form.id}>
                <TableCell className="font-medium">{form.formTitle}</TableCell>
                <TableCell>
                  {Object.keys(form.formSchemaJson?.properties ?? {}).length}
                </TableCell>
                <TableCell>{form.numberOfResponses}</TableCell>
                <TableCell>{updatedFormat.format(new Date(form.updatedAt))}</TableCell>
                <TableCell className="text-right">
                  <ShowFor permissions={['FORM_WRITE']}>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`${CUSTOM_FORMS_PATH}/${form.id}`)}
                      >
                        <Pencil className="size-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFormToDelete(form)}
                      >
                        <Trash2 className="size-4" />
                        Delete
                      </Button>
                    </div>
                  </ShowFor>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ConfirmationDialog
        open={!!formToDelete}
        onOpenChange={(open) => {
          if (!open) setFormToDelete(null);
        }}
        onSubmit={() => {
          if (formToDelete) deleteMutation.mutate(formToDelete.id);
        }}
        match={formToDelete?.formTitle ?? ''}
        title="Delete custom form"
        description={`Delete "${formToDelete?.formTitle}"? It disappears from this list and from the flow builder. A form used by a flow stage can't be deleted.`}
        matchLabel="Type the form title to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
